import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, combineLatest, map, Observable, of } from 'rxjs';
import { CreateWeightEntryCommand } from './CreateWeightEntryCommand';
import { Medicine } from "./Medicine";
import { MedicineDto } from './MedicineDto';
import { NotFound } from "./not-found";
import { Rabbit } from "./Rabbit";
import { RabbitDto } from './RabbitDto';
import { UpdateWeightEntryCommand } from './UpdateWeightEntryCommand';
import { WeightEntry } from './WeightEntry';
import { WeightEntryDto } from './WeightEntryDto';
import { environment } from '../../environments/environment';
import { CreateRabbitCommand } from './CreateRabbitCommand';
import { UpdateRabbitCommand } from './UpdateRabbitCommand';

@Injectable({
  providedIn: 'root'
})
export class ApiClientService {
  private host: string = environment.api.host;

  constructor(private readonly http: HttpClient) {
  }

  getWeightEntries(): Observable<WeightEntryDto[]> {
    return this.http.get<WeightEntryDto[]>(`${this.host}/weightentries`);
  }

  getWeightEntryById(id: string): Observable<WeightEntryDto | NotFound> {
    return this.http.get<WeightEntryDto | NotFound>(`${this.host}/weightentries/${id}`)
      .pipe(
        catchError((error: HttpErrorResponse): Observable<NotFound> => {
          if (error.status === 404) {
            return of(new NotFound());
          }

          throw new Error(error.message);
        })
      );
  }

  getRabbits(): Observable<Rabbit[]> {
    return this.http.get<RabbitDto[]>(`${this.host}/rabbits`)
      .pipe(
        map(results => results.map(rabbit => <Rabbit>{
          id: rabbit.id,
          name: rabbit.name,
          isDisabled: rabbit.isDisabled
        }))
      );
  }

  getRabbitById(id: string): Observable<Rabbit | NotFound> {
    return this.http.get<RabbitDto | NotFound>(`${this.host}/rabbits/${id}`)
      .pipe(
        catchError((error: HttpErrorResponse): Observable<NotFound> => {
          if (error.status === 404) {
            return of(new NotFound());
          }

          throw new Error(error.message);
        })
      );
  }

  deleteRabbits(rabbitId: string): Observable<unknown> {
    return this.http.delete(`${this.host}/rabbits/${rabbitId}`);
  }

  createRabbit(
    name: string,
    isDisabled: boolean
  ): Observable<any> {
    const request: CreateRabbitCommand = {
      name: name,
      isDisabled: isDisabled
    };
    return this.http.post(
      `${this.host}/rabbits`,
      request,);
    }

  updateRabbit(
    id: string,
    name: string,
    isDisabled: boolean
  ): Observable<any> {
    const request: UpdateRabbitCommand = {
      id: id,
      name: name,
      isDisabled: isDisabled
    };
    return this.http.put(
      `${this.host}/rabbits/${id}`,
      request,);
    }

  getMedicines(): Observable<Medicine[]> {
    return this.http.get<MedicineDto[]>(`${this.host}/medicines`)
      .pipe(
        map(medicines => medicines.map(medicine => <Medicine>{
          id: medicine.id,
          name: medicine.name,
          abbreviation: medicine.abbreviation
        })
        )
      );
  }

  getPreparedWeightEntries(): Observable<WeightEntry[]> {
    return combineLatest([this.getWeightEntries(), this.getRabbits(), this.getMedicines()])
      .pipe(
        map(apiResults => apiResults[0].map<WeightEntry>(entry => ({
          id: entry.id,
          weighedAt: entry.weighedAt,
          weight: entry.weight,
          rabbitId: entry.rabbitId,
          comment: entry.comment,
          medicines: entry.medicines,
          rabbit: apiResults[1].find(rabbit => rabbit.id === entry.rabbitId)!,
          medicineObjects: apiResults[2].filter(medicineObject => entry.medicines.includes(medicineObject.id))
        })
        ))
      )
  }

  getPreparedWeightEntryById(id: string): Observable<WeightEntry | NotFound> {
    return combineLatest([this.getWeightEntryById(id), this.getRabbits(), this.getMedicines()])
      .pipe(
        map((result: [WeightEntryDto | NotFound, RabbitDto[], MedicineDto[]]): WeightEntry | NotFound => {
          if (result[0] instanceof NotFound) {
            return result[0];
          }

          const entry = result[0];
          return <WeightEntry>{
            id: entry.id,
            weighedAt: entry.weighedAt,
            weight: entry.weight,
            rabbitId: entry.rabbitId,
            comment: entry.comment,
            medicines: entry.medicines,
            rabbit: result[1].find(rabbit => rabbit.id === entry.rabbitId)!,
            medicineObjects: result[2].filter(medicineObject => entry.medicines.includes(medicineObject.id))
          };
        })
      )
  }

  createWeightEntry(
    weighedAt: Date,
    weight: number | null,
    comment: string | null,
    rabbitId: string,
    medicines: string[]
  ): Observable<any> {
    const request: CreateWeightEntryCommand = {
      weighedAt: weighedAt,
      weight: weight,
      comment: comment,
      rabbitId: rabbitId,
      medicines: medicines,
    };
    return this.http.post(
      `${this.host}/weightentries`,
      request,
      {
        headers: { "api-version": "2.0" }
      });
  }

  updateWeightEntry(
    id: string,
    weighedAt: Date,
    weight: number | null,
    comment: string | null,
    rabbitId: string,
    medicines: string[]
  ): Observable<any> {
    const request: UpdateWeightEntryCommand = {
      id: id,
      weighedAt: weighedAt,
      weight: weight,
      comment: comment,
      rabbitId: rabbitId,
      medicines: medicines,
    };
    return this.http.put(
      `${this.host}/weightentries/${id}`,
      request,
      {
        headers: { "api-version": "2.0" }
      });
  }

  deleteWeightEntryById(id: string): Observable<unknown> {
    return this.http.delete(`${this.host}/weightentries/${id}`);
  }
}
